/**
 * Structures dropped from an earlier supplier RFQ once a later package covers them.
 * The original scope stays on the package; archived ids are hidden in the portal.
 */
import type { RfqPackage, TimberSupplierStructureRef } from '../types';
import { packageHubReleaseIds } from './rfqPackageReleases';
import { isReferenceRelease } from './rfqReleasePurpose';

export function archivedStructureIdSet(ids: readonly string[] | null | undefined): Set<string> {
  return new Set((ids ?? []).map((id) => String(id || '').trim()).filter(Boolean));
}

export function activeScopeStructures(
  structures: readonly TimberSupplierStructureRef[] | null | undefined,
  archivedIds: readonly string[] | null | undefined,
): TimberSupplierStructureRef[] {
  const archived = archivedStructureIdSet(archivedIds);
  const out: TimberSupplierStructureRef[] = [];
  const seen = new Set<string>();
  for (const s of structures ?? []) {
    const id = String(s?.id || '').trim();
    if (!id || archived.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: (s.name || '').trim() });
  }
  return out;
}

/** True when the package recorded a structure scope and every one of those structures is archived. */
export function packageScopeFullyArchived(
  pkg: Pick<RfqPackage, 'scopeStructures' | 'archivedStructureIds'>,
): boolean {
  const scoped = (pkg.scopeStructures ?? []).filter((s) => String(s?.id || '').trim());
  if (!scoped.length) return false;
  return activeScopeStructures(scoped, pkg.archivedStructureIds).length === 0;
}

export function structureIdsOnPackage(
  pkg: Pick<RfqPackage, 'scopeStructures'>,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const s of pkg.scopeStructures ?? []) {
    const id = String(s?.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Structure ids on `older` that a later package for the same supplier still asks
 * them to quote. Those are the ones to archive off the earlier request.
 */
export function structureIdsSupersededByLaterPackage(
  older: Pick<RfqPackage, 'id' | 'supplierOrgId' | 'createdAt' | 'scopeStructures' | 'archivedStructureIds'>,
  laterPackages: ReadonlyArray<
    Pick<RfqPackage, 'id' | 'supplierOrgId' | 'createdAt' | 'scopeStructures' | 'archivedStructureIds' | 'releasePurpose'>
  >,
): string[] {
  const org = (older.supplierOrgId || '').trim();
  if (!org) return [];
  const olderAt = older.createdAt instanceof Date ? older.createdAt.getTime() : 0;
  const already = archivedStructureIdSet(older.archivedStructureIds);
  const laterIds = new Set<string>();
  for (const pkg of laterPackages) {
    if (!pkg.id || pkg.id === older.id) continue;
    if (isReferenceRelease(pkg)) continue;
    if ((pkg.supplierOrgId || '').trim() !== org) continue;
    const at = pkg.createdAt instanceof Date ? pkg.createdAt.getTime() : 0;
    if (at <= olderAt) continue;
    for (const s of activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds)) {
      laterIds.add(s.id);
    }
  }
  return structureIdsOnPackage(older).filter((id) => laterIds.has(id) && !already.has(id));
}

export interface SupersededStructureArchiveRow {
  id: string;
  name: string;
  packageIds: string[];
}

/** Structures a later package still asks this supplier to quote, grouped for one Remove action. */
export function supersededStructureArchiveRows(
  requestPackages: ReadonlyArray<
    Pick<RfqPackage, 'id' | 'supplierOrgId' | 'createdAt' | 'scopeStructures' | 'archivedStructureIds'>
  >,
  allPackages: ReadonlyArray<
    Pick<RfqPackage, 'id' | 'supplierOrgId' | 'createdAt' | 'scopeStructures' | 'archivedStructureIds'>
  >,
): SupersededStructureArchiveRow[] {
  const dropById = new Map<string, SupersededStructureArchiveRow>();
  for (const pkg of requestPackages) {
    if (!pkg.id) continue;
    for (const id of structureIdsSupersededByLaterPackage(pkg, allPackages)) {
      const name = pkg.scopeStructures?.find((s) => s.id === id)?.name || id;
      const row = dropById.get(id) ?? { id, name, packageIds: [] };
      if (!row.packageIds.includes(pkg.id)) row.packageIds.push(pkg.id);
      dropById.set(id, row);
    }
  }
  return Array.from(dropById.values());
}

type ArchivedReleasePackage = Pick<
  RfqPackage,
  | 'id'
  | 'supplierOrgId'
  | 'createdAt'
  | 'hubFileReleaseId'
  | 'hubFileReleaseIds'
  | 'scopeStructures'
  | 'archivedStructureIds'
  | 'requestLabel'
  | 'label'
>;

/**
 * Hover copy when every structure on a staged release was archived off its
 * package. Null when the release is still open.
 */
export function archivedReleaseTooltip(
  release: { id?: string; structureIds?: string[] },
  packages: ReadonlyArray<ArchivedReleasePackage>,
): string | null {
  const releaseId = (release.id || '').trim();
  const structureIds = (release.structureIds ?? []).map((id) => String(id || '').trim()).filter(Boolean);
  if (!releaseId || !structureIds.length) return null;
  const owners = packages.filter((pkg) => packageHubReleaseIds(pkg).includes(releaseId));
  if (!owners.length) return null;
  const archived = structureIds.every((id) =>
    owners.every((pkg) => archivedStructureIdSet(pkg.archivedStructureIds).has(id)),
  );
  if (!archived) return null;

  const laterLabels: string[] = [];
  const seen = new Set<string>();
  for (const owner of owners) {
    const org = (owner.supplierOrgId || '').trim();
    const ownerAt = owner.createdAt instanceof Date ? owner.createdAt.getTime() : 0;
    for (const pkg of packages) {
      if (!pkg.id || pkg.id === owner.id) continue;
      if ((pkg.supplierOrgId || '').trim() !== org) continue;
      const at = pkg.createdAt instanceof Date ? pkg.createdAt.getTime() : 0;
      if (at <= ownerAt) continue;
      const covers = activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds).some((s) =>
        structureIds.includes(s.id),
      );
      if (!covers) continue;
      const label = (pkg.requestLabel || pkg.label || '').trim();
      if (label && !seen.has(label)) {
        seen.add(label);
        laterLabels.push(label);
      }
    }
  }

  if (laterLabels.length === 1) {
    return `Archived off this request because “${laterLabels[0]}” covers this structure. The supplier is not asked to quote it here.`;
  }
  if (laterLabels.length > 1) {
    return `Archived off this request because a later RFQ covers this structure (${laterLabels.join('; ')}). The supplier is not asked to quote it here.`;
  }
  return 'Archived off this request because a later RFQ covers this structure. The supplier is not asked to quote it here.';
}
